
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Timer, AlertTriangle, Save } from "lucide-react";
import { PrayerTime } from "@/types/page";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { 
  TooltipProvider, 
  Tooltip, 
  TooltipTrigger, 
  TooltipContent 
} from "@/components/ui/tooltip";
import PrayerTimesList from "./PrayerTimesList";

interface EditorFormProps {
  blockTitle: string;
  onBlockTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  animationSpeed: number;
  onAnimationSpeedChange: (value: number[]) => void;
  prayerTimes: PrayerTime[];
  onAddPrayerTime: () => void;
  onUpdatePrayerTime: (updatedPrayerTime: PrayerTime) => void;
  onRemovePrayerTime: (id: string) => void;
  onReorderPrayerTimes: (items: PrayerTime[]) => void;
  onSaveChanges: () => void;
  hasChanges: boolean;
  isLegacyFormat: boolean;
  convertToModernFormat: () => void;
}

const EditorForm: React.FC<EditorFormProps> = ({
  blockTitle,
  onBlockTitleChange,
  animationSpeed,
  onAnimationSpeedChange,
  prayerTimes,
  onAddPrayerTime,
  onUpdatePrayerTime,
  onRemovePrayerTime,
  onReorderPrayerTimes,
  onSaveChanges,
  hasChanges,
  isLegacyFormat,
  convertToModernFormat
}) => {
  return (
    <div className="space-y-4">
      {/* Header with Format Conversion Button */}
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium">עריכת זמני תפילה</h3>
          {isLegacyFormat && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-help ml-2">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    פורמט ישן
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>דף זה משתמש בפורמט ישן. המרה לפורמט החדש תאפשר עריכה מתקדמת יותר.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex gap-2">
          {isLegacyFormat && (
            <Button 
              onClick={convertToModernFormat} 
              variant="outline"
              className="text-blue-600 border-blue-600 hover:bg-blue-50"
            >
              המר לפורמט חדש
            </Button>
          )}
        </div>
      </div>
      
      {/* Block Title Editor */}
      <div className="mb-4">
        <Label htmlFor="blockTitle">כותרת הבלוק</Label>
        <Input
          id="blockTitle"
          value={blockTitle}
          onChange={onBlockTitleChange}
          placeholder="זמני תפילה"
          className="mt-1"
        />
      </div>

      {/* Animation Speed Controller */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Timer className="h-5 w-5 text-gray-500" />
          <Label htmlFor="animationSpeed">מהירות האנימציה</Label>
        </div>
        <div className="flex items-center gap-4">
          <Slider
            id="animationSpeed"
            min={5}
            max={60}
            step={1}
            value={[animationSpeed]}
            onValueChange={onAnimationSpeedChange}
            className="flex-1"
          />
          <span className="text-sm text-gray-500 min-w-[3rem] text-center">
            {animationSpeed} שניות
          </span>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-medium">זמני תפילה</h3>
          <span className="text-sm text-gray-500">יש {prayerTimes.length} זמנים</span>
        </div>
        
        <PrayerTimesList 
          prayerTimes={prayerTimes}
          onUpdate={onUpdatePrayerTime}
          onRemove={onRemovePrayerTime}
          onReorder={onReorderPrayerTimes}
        />
      </div>
      
      <div className="flex flex-col md:flex-row gap-2">
        <Button
          onClick={onAddPrayerTime}
          className="flex-1"
          type="button"
        >
          <Plus className="mr-2 h-4 w-4" /> הוסף זמן תפילה
        </Button>
        
        <Button
          onClick={onSaveChanges}
          className="flex-1 bg-green-600 hover:bg-green-700"
          type="button"
          disabled={!hasChanges}
        >
          <Save className="mr-2 h-4 w-4" /> שמור שינויים
        </Button>
      </div>
      
      {hasChanges && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-center">
          <p className="text-amber-800">יש שינויים שלא נשמרו</p>
        </div>
      )}
      
      {isLegacyFormat && (
        <div className="mt-4 p-3 border border-amber-300 bg-amber-50 rounded">
          <h4 className="text-amber-800 font-medium flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2" />
            שים לב: דף בפורמט ישן
          </h4>
          <p className="text-amber-700 text-sm mt-1">
            דף זה משתמש בפורמט ישן. ניתן להשתמש בכפתור "המר לפורמט חדש" כדי לעדכן את הדף לפורמט החדש, המאפשר פונקציונליות נוספת.
          </p>
        </div>
      )}
    </div>
  );
};

export default EditorForm;
